// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Search } from '@carbon/react';
import { Close } from '@carbon/icons-react';
import { useAudit } from '../../hooks/useAlarms';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useUrlState } from '../../hooks/useUrlState';
import { alarmDetailPath } from '../../services/alarmSelectors';
import { AUDIT_ACTORS } from '../../mocks/audit';
import { PageHead, SevTag, EmptyState, Loading } from '../../components/shell/ScreenHelpers';
import './AuditScreen.css';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export default function AuditScreen() {
  const navigate = useNavigate();
  // Audit entries carry no site of their own, so "Go to source" has always
  // resolved against the active site — implicitly, via the alarm page's
  // fallback. Threading it explicitly makes the resulting URL shareable and
  // refresh-safe. A genuinely site-correct link needs a site on AuditEntry.
  const { siteId: activeSiteId } = useActiveSite();
  // Filters and the open entry live in the URL, so a refresh or a shared link
  // lands on the same view. The drawer holds only the entry id; an id that
  // isn't in the loaded list leaves the drawer closed.
  const [{ actor, q: query, entry }, patchUrl] = useUrlState({ actor: 'all', q: '', entry: '' });
  const { entries, loading } = useAudit({ query, actor });
  const selected = (entry && entries.find(e => e.id === entry)) || null;
  // Opening an entry is a new place the user goes Back from; closing is not.
  const open = (id: string) => patchUrl({ entry: id }, { push: true });
  const close = () => patchUrl({ entry: null });

  const detail: [string, React.ReactNode][] = selected
    ? [
        ['Alarm', [selected.alarmId, selected.ruleId].filter(Boolean).join(' · ') || undefined],
        ['Priority', selected.sev ? <SevTag sev={selected.sev} /> : undefined],
        ['Asset', selected.asset],
        ['Category', selected.category],
        ['Source system', selected.srcSystem],
        ['Bound SOP', selected.sopId],
      ]
    : [];

  return (
    <>
      <PageHead title="Audit" dateline="System of record · immutable event trail" />

      <div className="hs-toolbar">
        {AUDIT_ACTORS.map(a => (
          <button
            key={a}
            type="button"
            className={`hs-pill${actor === a ? ' active' : ''}`}
            onClick={() => patchUrl({ actor: a })}
          >
            {a === 'helios' ? 'EnergyFluo' : cap(a)}
          </button>
        ))}
        <div className="aud-search">
          <Search
            size="lg"
            labelText="Search audit trail"
            placeholder="Search entries, assets, alarm IDs…"
            value={query}
            onChange={e => patchUrl({ q: e.target.value })}
          />
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : entries.length === 0 ? (
        <EmptyState big="No audit entries." />
      ) : (
        <table className="aud-table">
          <thead>
            <tr>
              <th className="aud-col-time">Time</th>
              <th className="aud-col-actor">Actor</th>
              <th>Entry</th>
              <th className="aud-col-src">Source</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr
                key={e.id}
                className={selected?.id === e.id ? 'active' : ''}
                role="button"
                data-testid="audit-row"
                data-audit-id={e.id}
                tabIndex={0}
                aria-label={`Open audit entry ${e.alarmId ?? e.id}`}
                onClick={() => open(e.id)}
                onKeyDown={ev => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    open(e.id);
                  }
                }}
              >
                <td className="aud-time">{e.t}</td>
                <td className="aud-actor" data-testid="audit-actor">
                  {e.whoLabel}
                </td>
                <td className="aud-entry">{e.text}</td>
                <td className="aud-src">{e.srcSystem ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <>
          <div className="aud-scrim" onClick={close} />
          <aside
            className="aud-panel"
            role="dialog"
            aria-label="Audit entry detail"
            data-testid="audit-detail"
          >
            <div className="aud-panel-head">
              <div className="aud-panel-title" data-testid="audit-title">
                {selected.whoLabel}
              </div>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                iconDescription="Close"
                renderIcon={Close}
                onClick={close}
              />
            </div>
            <div className="aud-panel-time">{selected.t}</div>
            <p className="aud-panel-text">{selected.text}</p>

            <dl className="aud-panel-meta">
              {detail.map(([k, v]) =>
                v ? (
                  <div key={k} className="aud-panel-metaitem">
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ) : null
              )}
            </dl>

            {selected.alarmId && (
              <Button
                size="md"
                onClick={() =>
                  navigate(
                    alarmDetailPath({ id: selected.alarmId as string, siteId: activeSiteId })
                  )
                }
              >
                Go to source
              </Button>
            )}
          </aside>
        </>
      )}
    </>
  );
}
